import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Trash2 } from "lucide-react";
import { useState } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { webRiskOverrideWarning } from "../../../../../shared/net-fetch-denial";
import {
  addNetGrant,
  listGrants,
  webriskCheck,
  type GrantKey,
  type OriginVerdict,
  type PolicyGrant,
  revokeGrant,
} from "../../../../policy/api";

export const Route = createFileRoute("/_workspace/settings/policy/")({
  component: PolicyPane,
});

const GRANTS_KEY = ["policy-grants"] as const;

function PolicyPane() {
  const grants = useQuery({
    queryKey: GRANTS_KEY,
    queryFn: listGrants,
  });

  return (
    <div className="mx-auto w-full max-w-3xl px-6 pt-8 pb-12">
      <h1 className="text-lg font-semibold">Policy</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Configure what agents, apps, and workspace members can access
      </p>

      <section className="mt-6">
        <AddNetGrantForm />
      </section>

      <section className="mt-8">
        <h2 className="text-sm font-semibold">Active grants</h2>
        <div className="mt-3">
          {grants.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : grants.error ? (
            <p className="text-sm text-destructive">
              {(grants.error as Error).message}
            </p>
          ) : grants.data && grants.data.grants.length > 0 ? (
            <GrantTable grants={grants.data.grants} />
          ) : (
            <p className="text-sm text-muted-foreground">
              No grants yet. Approvals the agent requests, and network grants
              you add above, appear here.
            </p>
          )}
        </div>
      </section>
    </div>
  );
}

function GrantTable({ grants }: { grants: PolicyGrant[] }) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Domain</TableHead>
          <TableHead>Resource</TableHead>
          <TableHead>Granted to</TableHead>
          <TableHead>Access</TableHead>
          <TableHead>Expires</TableHead>
          <TableHead className="text-right">Actions</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {grants.map((g) => (
          <GrantRow
            key={`${g.domain} ${g.principal} ${g.resource}`}
            grant={g}
          />
        ))}
      </TableBody>
    </Table>
  );
}

function GrantRow({ grant }: { grant: PolicyGrant }) {
  const qc = useQueryClient();
  const revoke = useMutation({
    mutationFn: (key: GrantKey) => revokeGrant(key),
    onSuccess: () => qc.invalidateQueries({ queryKey: GRANTS_KEY }),
  });

  const expired = grant.expiresAt != null && grant.expiresAt <= Date.now();

  return (
    <TableRow className={expired ? "opacity-60" : undefined}>
      <TableCell>
        <DomainBadge domain={grant.domain} />
      </TableCell>
      <TableCell>
        <span className="break-all font-medium">{grant.resource}</span>
      </TableCell>
      <TableCell className="text-muted-foreground">{grant.principal}</TableCell>
      <TableCell className="text-muted-foreground">
        {grant.actions === "*" ? "all" : grant.actions.join(", ")}
      </TableCell>
      <TableCell className="text-muted-foreground">
        {formatExpiry(grant.expiresAt, expired)}
      </TableCell>
      <TableCell className="text-right">
        <Button
          variant="ghost"
          size="icon"
          className="text-muted-foreground hover:text-destructive"
          disabled={revoke.isPending}
          title="Revoke"
          aria-label="Revoke"
          onClick={() =>
            revoke.mutate({
              domain: grant.domain,
              principal: grant.principal,
              resource: grant.resource,
            })
          }
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function DomainBadge({ domain }: { domain: string }) {
  const variant =
    domain === "net" ? "default" : domain === "fs" ? "secondary" : "outline";
  const label =
    domain === "net" ? "network" : domain === "fs" ? "files" : domain;
  return <Badge variant={variant}>{label}</Badge>;
}

function formatExpiry(expiresAt: number | null, expired: boolean): string {
  if (expiresAt == null) return "Never";
  const date = new Date(expiresAt).toLocaleDateString();
  return expired ? `Expired ${date}` : date;
}

const EXPIRY_OPTIONS = [
  { value: "30", label: "30 days" },
  { value: "90", label: "90 days" },
  { value: "never", label: "No expiry" },
] as const;

const DEFAULT_EXPIRY = "30";

const DAY_MS = 24 * 60 * 60 * 1000;

function AddNetGrantForm() {
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [expiry, setExpiry] = useState<string>(DEFAULT_EXPIRY);
  // A Web Risk-flagged target parks here for the § 2.5 override confirmation
  // instead of granting on the first click. The grant that follows is the
  // ordinary allow grant — the warning is the variant, not the mechanism.
  const [flaggedAsk, setFlaggedAsk] = useState<{
    url: string;
    expiresAt: number | null;
    verdict: OriginVerdict;
  } | null>(null);

  const add = useMutation({
    mutationFn: async ({
      url,
      expiresAt,
      overrideFlag,
    }: {
      url: string;
      expiresAt: number | null;
      /** Set once the member confirmed the Web Risk override warning. */
      overrideFlag?: boolean;
    }) => {
      if (!overrideFlag) {
        // One checker call before granting: a flagged origin must interrupt
        // with the alarming variant, never grant on the ordinary click.
        // `unavailable` (or a failed check) falls through to the grant — the
        // member is explicitly deciding, which is precisely the override the
        // fail-closed default leaves room for.
        const verdicts = await webriskCheck([url]).catch(() => null);
        const verdict = verdicts ? Object.values(verdicts)[0] : undefined;
        if (verdict?.status === "flagged") {
          setFlaggedAsk({ url, expiresAt, verdict });
          return null;
        }
      }
      // "Allow this site" grants the whole origin — net is single-action, so
      // the managed browser and the server proxy alike may reach it.
      return addNetGrant(url, { expiresAt });
    },
    onSuccess: (granted) => {
      if (!granted) return; // parked on the flagged confirmation
      qc.invalidateQueries({ queryKey: GRANTS_KEY });
      setUrl("");
      setExpiry(DEFAULT_EXPIRY);
      setFlaggedAsk(null);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        const value = url.trim();
        if (!value) return;
        setFlaggedAsk(null);
        const days = expiry === "never" ? null : Number(expiry);
        const expiresAt = days == null ? null : Date.now() + days * DAY_MS;
        add.mutate({ url: value, expiresAt });
      }}
    >
      <h2 className="text-sm font-semibold">Agent internet access</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        Add sites to allow Agent Heath to access them.
      </p>
      <div className="mt-3 flex max-w-xl flex-wrap items-center gap-2">
        <Input
          type="text"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://example.com"
          className="min-w-56 flex-1"
          disabled={add.isPending}
        />
        <Select
          value={expiry}
          onValueChange={setExpiry}
          disabled={add.isPending}
        >
          <SelectTrigger className="w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {EXPIRY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" disabled={!url.trim() || add.isPending}>
          {add.isPending ? "Adding…" : "Add grant"}
        </Button>
      </div>
      {flaggedAsk ? (
        <div className="mt-3 max-w-xl rounded-md border border-destructive/40 bg-destructive/10 p-3">
          <p className="text-sm font-medium text-destructive">
            This site was flagged by Google Web Risk
          </p>
          <p className="mt-1 text-xs text-destructive">
            {webRiskOverrideWarning(
              flaggedAsk.url,
              flaggedAsk.verdict.threatTypes ?? [],
            )}
          </p>
          <div className="mt-3 flex gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              disabled={add.isPending}
              onClick={() =>
                add.mutate({
                  url: flaggedAsk.url,
                  expiresAt: flaggedAsk.expiresAt,
                  overrideFlag: true,
                })
              }
            >
              Allow anyway (Web Risk override)
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={add.isPending}
              onClick={() => setFlaggedAsk(null)}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
      {add.error ? (
        <p className="mt-2 text-sm text-destructive">
          {(add.error as Error).message}
        </p>
      ) : null}
    </form>
  );
}
